import { ActionPayloadValidationError, requireStringArrayField, requireStringField } from "@slot/client-engine";
import type { ReplayContext, RoundAction, RoundActionHandler } from "@slot/client-engine";

/** One badged cell: its canonical linear grid index and its multiplier (a whole number, "2" and up by the rules). */
export interface CellMultiplierBadge {
  positionIndex: number;
  multiplier: string;
}

/** What the handler drives (the feature's `CellMultiplierPresenter`). */
export interface CellMultiplierTarget {
  /** The badges on the board from now on: every badged cell, each with its multiplier. */
  updateCellMultipliers(badges: readonly CellMultiplierBadge[]): Promise<void>;
}

/**
 * GAME-DEFINED action `update_cell_multipliers { positionMask, values }`: the cell multiplier badges on the board — EVERY
 * badged cell, as a grid-length "0/1" mask and one multiplier per "1" in index order (the canonical multiplier actions'
 * shape). A cell absent from it carries no badge. Sent after each winning substep's merge (the cluster cells' badges
 * landed) and at the start of every free spin, in its reveal's `frame_prepare` (the badges the spin starts with); a base
 * spin starts clean, which the client applies at its boundary.
 *
 * SERVER-AUTHORITATIVE: the client shows the badges it is handed and never computes one (x2 on a cell's first win, +1
 * after, the cap if any — all the server's). The backend and the fixtures author it never skippable, on phase "win"
 * after a merge: the badges are the board's state, and the wins after them read them.
 */
export class UpdateCellMultipliersActionHandler implements RoundActionHandler {
  public readonly actionTypeName = "update_cell_multipliers";

  public constructor(private readonly target: CellMultiplierTarget) {}

  public async handleAction(roundAction: RoundAction, _context: ReplayContext): Promise<void> {
    const payload = roundAction.actionPayload;
    const positionMask = requireStringField(this.actionTypeName, payload, "positionMask");
    if (!/^[01]+$/.test(positionMask)) throw new ActionPayloadValidationError(this.actionTypeName, "positionMask", `Must be a "0/1" mask, got ${JSON.stringify(positionMask)}.`);
    const values = requireStringArrayField(this.actionTypeName, payload, "values");
    const indices = [...positionMask].flatMap((bit, index) => (bit === "1" ? [index] : []));
    if (indices.length !== values.length) {
      throw new ActionPayloadValidationError(this.actionTypeName, "values", `values.length (${values.length}) must match the mask's "1" count (${indices.length}).`);
    }
    for (const [i, value] of values.entries()) {
      if (!/^[1-9]\d*$/.test(value)) throw new ActionPayloadValidationError(this.actionTypeName, `values[${i}]`, `Must be a whole number of at least 1, got ${JSON.stringify(value)}.`);
    }
    await this.target.updateCellMultipliers(indices.map((positionIndex, i) => ({ positionIndex, multiplier: values[i]! })));
  }
}
